import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useTranslation } from 'Shared/utils/hooks/use-translation';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Types
import { GoalItem } from 'Shared/constants/types';

// Actions
import { createComment } from 'Shared/actions/Comments.actions';

// Componentts
import WpAvatar from 'Wpds/components/avatar/WpAvatar';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import CommentTextEditor from 'Shared/components/Comments/CommentTextEditor/CommentTextEditor';

// Styles
import './GoalCommentInput.scss';

type Props = {
  goal: GoalItem;
  placeholder?: string;
  scrollToCallback?: Function;
};

const GoalCommentInput = ({ goal, scrollToCallback, placeholder = 'Add a comment...' }: Props) => {
  const { tr } = useTranslation();
  const dispatch = useDispatch();
  const { avatar } = useCurrentUser();

  const [isOpen, toggleIsOpen] = useToggle(false);
  const [isSaving, toggleIsSaving] = useToggle(false);

  const handleOpenCommentInput = () => {
    toggleIsOpen();
    // need a little timeout to allow for the comment
    // input to open up
    if (scrollToCallback) {
      setTimeout(() => {
        scrollToCallback();
      }, 1);
    }
  };

  const handleCreateComment = useCallback(
    content => {
      toggleIsSaving();
      dispatch(createComment({ record: 'goal', parentId: goal.slug, payload: content })).then(() => {
        toggleIsOpen();
        toggleIsSaving();
        if (scrollToCallback) scrollToCallback();
      });
    },
    [dispatch, goal.slug]
  );

  const Input = (
    <CommentTextEditor
      workspaceId={goal.workspaceSlug}
      handleCreateComment={handleCreateComment}
      hideVideo
      autoFocus
      toolbarPlacement="bottom"
    />
  );

  const baseClassName = 'GoalCommentInput';
  return (
    <div className={baseClassName}>
      {isSaving && (
        <div className={`${baseClassName}__saving`}>
          <div className={`${baseClassName}__saving__pill`}>
            <svg width={20} height={20} viewBox="0 0 50 50" className={`${baseClassName}__saving__pill__spinner`}>
              <path d="M43.935 25.145c0-10.318-8.364-18.683-18.683-18.683-10.318 0-18.683 8.365-18.683 18.683h4.068c0-8.071 6.543-14.615 14.615-14.615s14.615 6.543 14.615 14.615h4.068z">
                <animateTransform
                  attributeType="xml"
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
              </path>
            </svg>
            {tr('perform.goals.comments.saving')}
          </div>
        </div>
      )}
      <div className={`${baseClassName}__avatar`}>
        <WpAvatar avatar={avatar} size="sm" />
      </div>
      <div className={`${baseClassName}__input`}>
        {!isOpen ? (
          <div
            className={`${baseClassName}__placeholder`}
            onClick={handleOpenCommentInput}
            data-test="MeetingItemCommentsInput-placeholder-wrapper"
          >
            <WpTextField
              id="enter-comment-slim"
              label={tr('perform.goals.comments.add_comment_label')}
              labelHidden
              placeholder={placeholder || tr('perform.goals.comments.leave_comment_placeholder')}
            />
          </div>
        ) : (
          <>{Input}</>
        )}
      </div>
    </div>
  );
};

export default GoalCommentInput;
